// Pure, client-safe helpers for free-access list resolution. Used by the
// Account / Subscription page (Sponsored Alerts section) and the limit
// modal (pause-options filter). Kept out of AvailabilityRequest.js so
// client bundles don't drag in `db.server` via a transitive import.

import {
  getActiveFacilityFreeAccessOffers,
  isFreeAccessFacility,
} from "../config/freeAccessOffers.js";

// Returns each active offer that at least one of the given availability
// requests qualifies for, deduplicated by `offer.key`. Order follows
// first-seen order across the input list, which keeps surface ordering
// stable for the user even as the list changes.
export function getQualifiedFreeAccessOffers(availabilityRequests = []) {
  const offersByKey = new Map();

  for (const availabilityRequest of availabilityRequests) {
    const offers = getActiveFacilityFreeAccessOffers(
      availabilityRequest?.facility,
    );

    for (const offer of offers) {
      if (!offersByKey.has(offer.key)) {
        offersByKey.set(offer.key, offer);
      }
    }
  }

  return [...offersByKey.values()];
}

export function getPrimaryFreeAccessOffer(availabilityRequests = []) {
  return getQualifiedFreeAccessOffers(availabilityRequests)[0] ?? null;
}

// Returns the active requests a user could trade in to free a cap slot,
// plus a count of free-access requests excluded from that list. Free-access
// requests don't count against the cap, so pausing one doesn't open a new
// slot — the modal needs both numbers to render the right empty-state copy.
export function partitionPauseableActive(activeRequests = []) {
  const pauseable = [];
  let freeAccessCount = 0;

  for (const request of activeRequests) {
    if (isFreeAccessFacility(request?.facility)) {
      freeAccessCount += 1;
    } else {
      pauseable.push(request);
    }
  }

  return { pauseable, freeAccessCount };
}
